import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import StockHistoryModal from './StockHistoryModal';

const StockReport = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [lastPage, setLastPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showStockHistoryModal, setShowStockHistoryModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    const [totals, setTotals] = useState(null);
    
    // Filter options from API
    const [filters, setFilters] = useState({
        locations: [],
        categories: [],
        brands: [],
        units: []
    });

    // Selected filter values
    const [selectedFilters, setSelectedFilters] = useState({
        location_id: '',
        category_id: '',
        brand_id: '',
        unit_id: '',
        type: '',
        active_state: ''
    });

    // Fetch filters on mount
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const res = await apiService.getStockReportFilters();
                if (res.success) {
                    setFilters(res.data);
                }
            } catch (err) {
      /* console.error('Failed to load filters:', err); */
            }
        };
        fetchFilters();
    }, []);

    const fetchReport = useCallback(async () => {
        setIsLoading(true);
        try {
            let productId = null;
            
            // If there's a search term, try to resolve it to a product ID first
            if (searchTerm && searchTerm.trim()) {
                try {
                    const prodRes = await apiService.getProducts({ search: searchTerm.trim(), per_page: 5 });
                    const prodList = prodRes.data || (Array.isArray(prodRes) ? prodRes : []);
                    
                    if (prodList.length > 0) {
                        // Find exact SKU match if possible, otherwise use the first result
                        const exactMatch = prodList.find(p => String(p.sku).toLowerCase() === searchTerm.trim().toLowerCase());
                        productId = exactMatch ? exactMatch.id : prodList[0].id;
                    } else {
                        // No product found for this search term, return empty report
                        setReportData([]);
                        setTotalEntries(0);
                        setLastPage(1);
                        setIsLoading(false);
                        return;
                    }
                } catch (err) {
      /* console.error('Error resolving product search:', err); */
                }
            }

            const params = {
                ...selectedFilters,
                per_page: perPage,
                page: currentPage,
                product_id: productId
            };
            
            const res = await apiService.getStockReport(params);
            if (res.success) {
                // Handle both { data: { data: [] } } and { data: [] }
                const reportList = res.data?.data || (Array.isArray(res.data) ? res.data : []);
                setReportData(reportList);
                if (res.data?.totals) {
                    setTotals(res.data.totals);
                }
                
                // Handle pagination from res.data or root
                setTotalEntries(res.data?.total || res.total || reportList.length || 0);
                setLastPage(res.data?.last_page || res.last_page || 1);
            }
        } catch (err) {
      /* console.error('Failed to fetch stock report:', err); */
        } finally {
            setIsLoading(false);
        }
    }, [selectedFilters, perPage, currentPage, searchTerm]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleExport = (format) => {
        const headers = [
            'SKU', 'Product', 'Variation', 'Category', 'Location', 
            'Unit Selling Price', 'Current stock', 
            'Current Stock Value (By purchase price)', 
            'Current Stock Value (By sale price)', 
            'Potential profit'
        ];
        
        const rows = reportData.map(p => [
            p.sku,
            p.product,
            p.variation_name || 'Default',
            p.category_name,
            p.location_name,
            p.unit_price_formatted,
            p.stock_formatted,
            p.stock_price_formatted,
            p.stock_value_by_sale_price_formatted,
            p.potential_profit_formatted
        ]);

        const title = 'Stock Report';
        const filename = 'stock_report';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
    };

    const handleOpenStockHistory = (p) => {
        setSelectedProduct({ ...p, id: p.product_id, name: p.product });
        setShowStockHistoryModal(true);
    };

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* Metrics Grid */}
            <div className="stock-report-metrics">
                <div className="stock-report-metrics-item">
                    <div className="stock-report-metrics-label">Closing stock (By purchase price)</div>
                    <div className="stock-report-metrics-value">{totals?.closing_stock_by_pp_formatted || '$ 0.00'}</div>
                </div>
                <div className="stock-report-metrics-item">
                    <div className="stock-report-metrics-label">Closing stock (By sale price)</div>
                    <div className="stock-report-metrics-value">{totals?.closing_stock_by_sp_formatted || '$ 0.00'}</div>
                </div>
                <div className="stock-report-metrics-item">
                    <div className="stock-report-metrics-label">Potential profit</div>
                    <div className="stock-report-metrics-value" style={{ color: (totals?.potential_profit ?? 0) >= 0 ? '#10b981' : '#ef4444' }}>
                        {totals?.potential_profit_formatted || '$ 0.00'}
                    </div>
                </div>
                <div className="stock-report-metrics-item">
                    <div className="stock-report-metrics-label">Profit Margin %</div>
                    <div className="stock-report-metrics-value" style={{ color: '#3b82f6' }}>
                        {totals?.profit_margin_pct_formatted || '0.00%'}
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1rem 1.25rem', position: 'relative', zIndex: 20 }}>
                    <div className="table-controls-left">
                        <div className="entries-dropdown">
                            <span>Show</span>
                            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>

                    <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {/* Filters Button */}
                        <button
                            onClick={() => setShowFilters(true)}
                            style={{
                                padding: '0.4rem 0.8rem',
                                border: '1px solid var(--etpos-border, #e2e8f0)',
                                borderRadius: '6px',
                                background: 'var(--etpos-card, #fff)',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                color: 'var(--etpos-text, #334155)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                fontWeight: '500'
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            Filters
                        </button>

                        {/* Export Button */}
                        <div className="export-dropdown-container" style={{ position: 'relative', zIndex: 100 }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', background: 'var(--etpos-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', zIndex: 9999, minWidth: '160px' }}>
                                    {[
                                        { label: 'Export CSV', action: () => handleExport('CSV'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
                                        { label: 'Export Excel', action: () => handleExport('Excel'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M8 13h8"/><path d="M8 17h8"/></svg> },
                                        { label: 'Print', action: () => handleExport('Print'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg> },
                                        { label: 'Export PDF', action: () => handleExport('PDF'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15v-6h2a2 2 0 0 1 0 4H9"/></svg> }
                                    ].map((item, i) => (
                                        <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.55rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--etpos-bg, #f1f5f9)'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            {item.icon}
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Search Input */}
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ padding: '0.4rem 0.75rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px', background: 'var(--etpos-bg, #fff)', color: 'var(--etpos-text, inherit)' }}
                        />
                    </div>
                </div>

                <div className="products-table-container" style={{ overflowX: 'auto' }}>
                    {isLoading ? (
                        <div className="loading-sales">
                            <h3>⏳ Loading Stock Report...</h3>
                            <p>Fetching stock report data from server...</p>
                        </div>
                    ) : (
                    <table className="products-table" style={{ minWidth: '1300px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '120px' }}>Action</th>
                                <th>SKU</th>
                                <th style={{ minWidth: '240px' }}>Product</th>
                                <th>Variation</th>
                                <th>Category</th>
                                <th>Location</th>
                                <th>Unit Selling Price</th>
                                <th>Current stock</th>
                                <th>Current Stock Value (Purchase)</th>
                                <th>Current Stock Value (Sale)</th>
                                <th>Potential profit</th>
                                <th>Total unit sold</th>
                                <th>Total Unit Transfered</th>
                                <th>Total Unit Adjusted</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.length === 0 ? (
                                <tr><td colSpan="14" style={{ textAlign: 'center', padding: '3rem', color: 'var(--etpos-text-muted, #64748b)' }}>No stock data found</td></tr>
                            ) : (
                                reportData.map((p, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <button className="stock-history-btn" onClick={() => handleOpenStockHistory(p)}>
                                                <i className="fa fa-history"></i> Stock history
                                            </button>
                                        </td>
                                        <td>{p.sku}</td>
                                        <td style={{ minWidth: '240px', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{p.product}</td>
                                        <td>{p.variation_name || 'DUMMY'}</td>
                                        <td>{p.category_name}</td>
                                        <td>{p.location_name}</td>
                                        <td>{p.unit_price_formatted}</td>
                                        <td style={{ fontWeight: '600' }}>
                                            <span style={{ 
                                                padding: '0.2rem 0.5rem', 
                                                background: p.is_below_alert_quantity ? '#fef2f2' : '#f0fdf4',
                                                color: p.is_below_alert_quantity ? '#dc2626' : '#16a34a',
                                                borderRadius: '4px'
                                            }}>
                                                {p.stock_formatted && !p.stock_formatted.startsWith('0.0') ? p.stock_formatted : (p.current_stock || p.stock || p.qty_available || p.stock_formatted || '0')}
                                            </span>
                                        </td>
                                        <td>{p.stock_price_formatted}</td>
                                        <td>{p.stock_value_by_sale_price_formatted}</td>
                                        <td style={{ color: p.potential_profit >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                                            {p.potential_profit_formatted}
                                        </td>
                                        <td>{p.total_sold_formatted}</td>
                                        <td>{p.total_transferred_formatted}</td>
                                        <td>{p.total_adjusted_formatted}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: 'var(--etpos-bg, #f8fafc)', fontWeight: '700', borderTop: '2px solid var(--etpos-border, #cbd5e1)' }}>
                                <td colSpan="7" style={{ textAlign: 'right', padding: '0.75rem 1rem', color: 'var(--etpos-text, #1e293b)' }}>
                                    Total:
                                </td>
                                <td style={{ padding: '0.75rem 1rem', color: 'var(--etpos-text, #1e293b)' }}>
                                    {reportData.reduce((sum, p) => sum + (parseFloat(p.stock) || 0), 0).toFixed(2)}
                                </td>
                                <td style={{ padding: '0.75rem 1rem', color: 'var(--etpos-text, #1e293b)' }}>
                                    $ {reportData.reduce((sum, p) => sum + (parseFloat(p.stock_price) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td style={{ padding: '0.75rem 1rem', color: 'var(--etpos-text, #1e293b)' }}>
                                    $ {reportData.reduce((sum, p) => sum + (parseFloat(p.stock_value_by_sale_price) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td style={{ padding: '0.75rem 1rem', color: reportData.reduce((sum, p) => sum + (parseFloat(p.potential_profit) || 0), 0) >= 0 ? '#10b981' : '#ef4444' }}>
                                    $ {reportData.reduce((sum, p) => sum + (parseFloat(p.potential_profit) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td style={{ padding: '0.75rem 1rem', color: 'var(--etpos-text, #1e293b)' }}>
                                    {reportData.reduce((sum, p) => sum + (parseFloat(p.total_sold) || 0), 0).toFixed(2)}
                                </td>
                                <td style={{ padding: '0.75rem 1rem', color: 'var(--etpos-text, #1e293b)' }}>
                                    {reportData.reduce((sum, p) => sum + (parseFloat(p.total_transferred) || 0), 0).toFixed(2)}
                                </td>
                                <td style={{ padding: '0.75rem 1rem', color: 'var(--etpos-text, #1e293b)' }}>
                                    {reportData.reduce((sum, p) => sum + (parseFloat(p.total_adjusted) || 0), 0).toFixed(2)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid var(--etpos-border, #e2e8f0)', background: 'var(--etpos-card, white)' }}>
                    <div style={{ color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.875rem' }}>
                        Showing {reportData.length > 0 ? (currentPage - 1) * perPage + 1 : 0} to {Math.min(currentPage * perPage, totalEntries)} of {totalEntries} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #64748b)' }}>
                            Page {currentPage} of {lastPage}
                        </span>
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(lastPage, prev + 1))}
                            disabled={currentPage === lastPage}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter Modal Popup */}
            {showFilters && (
                <div className="modal-overlay" onClick={() => setShowFilters(false)}>
                    <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
                        <div className="modal-header">
                            <h2>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                                </svg>
                                Filters
                            </h2>
                            <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
                        </div>
                        <div className="modal-content" style={{ padding: '1.5rem' }}>
                            <div className="filter-row">
                                <div className="filter-group">
                                    <label>Business Location:</label>
                                    <select
                                        value={selectedFilters.location_id}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, location_id: e.target.value })}
                                    >
                                        <option value="">All Locations</option>
                                        {filters.locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Category:</label>
                                    <select
                                        value={selectedFilters.category_id}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, category_id: e.target.value })}
                                    >
                                        <option value="">All Categories</option>
                                        {filters.categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Brand:</label>
                                    <select
                                        value={selectedFilters.brand_id}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, brand_id: e.target.value })}
                                    >
                                        <option value="">All Brands</option>
                                        {filters.brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Unit:</label>
                                    <select
                                        value={selectedFilters.unit_id}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, unit_id: e.target.value })}
                                    >
                                        <option value="">All Units</option>
                                        {filters.units.map(unit => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="filter-row second-row">
                                <div className="filter-group">
                                    <label>Product Type:</label>
                                    <select
                                        value={selectedFilters.type}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, type: e.target.value })}
                                    >
                                        <option value="">All Types</option>
                                        <option value="single">Single</option>
                                        <option value="variable">Variable</option>
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Active State:</label>
                                    <select
                                        value={selectedFilters.active_state}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, active_state: e.target.value })}
                                    >
                                        <option value="">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Stock History Modal */}
            {showStockHistoryModal && (
                <StockHistoryModal
                    product={selectedProduct}
                    onClose={() => setShowStockHistoryModal(false)}
                />
            )}
        </div>
    );
};

export default StockReport;
